import React from "react";
import {
  Controller,
  FieldPath,
  FieldValues,
  RegisterOptions,
  UseFormReturn,
} from "react-hook-form";
import { DatePicker, DatePickerProps } from "../atoms/datePicker/DatePicker";
import { FormFieldControl } from "./FormFieldControl";

type DatePickerFieldProps<TFormValues extends FieldValues> = Omit<
  DatePickerProps,
  "value" | "onChange" | "isInvalid" | "isDisabled" | "isRequired"
> & {
  form: UseFormReturn<TFormValues>;
  name: FieldPath<TFormValues>;
  label?: React.ReactNode;
  rules?: RegisterOptions<TFormValues, FieldPath<TFormValues>>;
  helperText?: React.ReactNode;
  isRequired?: boolean;
  isDisabled?: boolean;
};

export function DatePickerField<TFormValues extends FieldValues>({
  form,
  name,
  rules,
  label,
  helperText,
  isRequired,
  isDisabled,
  ...datePickerProps
}: DatePickerFieldProps<TFormValues>): React.ReactElement {
  const requiredRule =
    rules?.required ??
    (isRequired ? `${label ?? "This field"} is required` : false);

  return (
    <FormFieldControl
      form={form}
      name={name}
      label={label}
      helperText={helperText}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      <Controller
        control={form.control}
        name={name}
        rules={{
          ...rules,
          required: requiredRule,
        }}
        render={({ field, fieldState }) => {
          // DatePicker does not use the form input context from the outer
          // FormControl, so we wire in the state manually.

          return (
            <DatePicker
              {...datePickerProps}
              // date picker uses undefined to represent an empty selection, but
              // RHF prefers to use an empty string.
              value={field.value || undefined}
              onChange={(date) => field.onChange(date ?? "")}
              isRequired={isRequired}
              isDisabled={isDisabled || form.formState.isSubmitting}
              isInvalid={!!fieldState.error && !form.formState.isSubmitting}
            />
          );
        }}
      />
    </FormFieldControl>
  );
}
